import { Tag, type TagProps } from 'antd'
import type { ColumnProps } from 'antd/lib/table'
import { noop } from 'lodash'
import { type RouteComponentProps, withRouter } from 'react-router'

import { formatDatetime } from '~/utils'
import { withScheme } from '~/components/Scheme'
import Table from '~/components/Table'
import * as routes from '~/constants/routes' // skipcq JS-C1003
import {
  SubscriptionStatus,
  useUserSubscriptionListQuery,
  type UserSubscriptionListQuery_user_User_user_subscriptions_UserSubscription as UserSubscriptionListItem,
} from '~/gql/generated/graphql'
import { SCHEME } from '~/types'

import SubscriptionActions from './SubscriptionActions'

const statusColours: Record<SubscriptionStatus, TagProps['color']> = {
  [SubscriptionStatus.pending]: 'blue',
  [SubscriptionStatus.active]: 'green',
  [SubscriptionStatus.past_due]: 'red',
  [SubscriptionStatus.ended]: 'default',
}

const statusLabels: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.pending]: 'Pending',
  [SubscriptionStatus.active]: 'Active',
  [SubscriptionStatus.past_due]: 'Past due',
  [SubscriptionStatus.ended]: 'Ended',
}

const willRenew = ({ status, cancel_at }: UserSubscriptionListItem) =>
  !cancel_at &&
  [SubscriptionStatus.active, SubscriptionStatus.past_due].includes(status)

const columns: ColumnProps<UserSubscriptionListItem>[] = [
  {
    title: 'ID',
    dataIndex: 'id',
  },
  {
    title: 'Started',
    dataIndex: 'created_at',
    render: text => formatDatetime(text),
  },
  {
    title: 'Product',
    dataIndex: ['scheme_product', 'name'],
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status: SubscriptionStatus) => (
      <Tag color={statusColours[status]}>{statusLabels[status]}</Tag>
    ),
  },
  {
    title: 'Per period',
    dataIndex: ['amount', 'formatted_amount'],
  },
  {
    title: 'Renews',
    dataIndex: 'current_period_end',
    // Held after it stops renewing, as the record of the last period paid for
    render: (text, record) =>
      text && willRenew(record) ? formatDatetime(text) : '-',
  },
  {
    title: 'Ends',
    dataIndex: 'cancel_at',
    render: text => (text ? formatDatetime(text) : '-'),
  },
]

const actionsColumn = (
  user_id: string,
): ColumnProps<UserSubscriptionListItem> => ({
  title: 'Actions',
  key: 'actions',
  align: 'right',
  // The row itself navigates to the product
  onCell: () => ({ onClick: event => event.stopPropagation() }),
  render: (_, subscription) => (
    <SubscriptionActions user_id={user_id} subscription={subscription} />
  ),
})

const UserSubscriptions = ({
  scheme,
  user_id,
  history,
}: {
  scheme: SCHEME
  user_id: string
} & RouteComponentProps) => {
  const { data, loading } = useUserSubscriptionListQuery({
    variables: { scheme_id: scheme.id, user_id },
    errorPolicy: 'all',
  })

  return (
    <Table<UserSubscriptionListItem>
      columns={[...columns, actionsColumn(user_id)]}
      data={{ table: { nodes: data?.user?.user_subscriptions ?? [] } }}
      loading={loading}
      onChange={noop}
      rowClassName={(record: UserSubscriptionListItem) =>
        record.status === SubscriptionStatus.ended ? 'inactive-row' : 'generic'
      }
      onRow={({ scheme_product }: UserSubscriptionListItem) => ({
        onClick: () => {
          if (scheme_product) {
            history.push(
              routes.prependScheme(
                `${routes.PRODUCTS}/${scheme_product.id}`,
                scheme_product.scheme_id,
              ),
            )
          }
        },
      })}
    />
  )
}

export default withRouter(withScheme(UserSubscriptions))
